import type { StateCreator } from 'zustand';
import type { Region } from '@/constants/regions';
import { REGIONS } from '@/constants/regions';
import type { AppPermissions } from '@/types';

export type { Region };

export enum ThoughtSpotSdkAuthStatus {
  Authenticated = 'authenticated',
  Unauthenticated = 'unauthenticated',
}

const ENV_VARS = {
  SOURCETABLE_ANSWER_IDS: 'VITE_SOURCETABLE_ANSWER_IDS',
  ALT_SOURCETABLE_ANSWER_IDS: 'VITE_ALT_SOURCETABLE_ANSWER_IDS',
  DATASETTABLE_ANSWER_IDS: 'VITE_DATASETTABLE_ANSWER_IDS',
  ALT_DATASETTABLE_ANSWER_IDS: 'VITE_ALT_DATASETTABLE_ANSWER_IDS',
  THOUGHTSPOT_URLS: 'VITE_THOUGHTSPOT_URLS',
  API_GATEWAY_URLS: 'VITE_API_GATEWAY_URLS',
  UNIFIED_PLATFORM_API_URLS: 'VITE_UNIFIED_PLATFORM_API_BASE_URL',
  SURVEY_URLS: 'VITE_UNINAV_SURVEY_URLS',
  DIGITAL_URLS: 'VITE_UNINAV_DIGITAL_URLS',
  DIGITAL_MARKETING_URLS: 'VITE_UNINAV_DIGITAL_MARKETING_URLS',
  WORKFLOW_URLS: 'VITE_UNINAV_WORKFLOW_URLS',
  PULSE_URLS: 'VITE_UNINAV_PULSE_URLS',
  PULSE_MARKETING_URLS: 'VITE_UNINAV_PULSE_MARKETING_URLS',
  REPUTATION_URLS: 'VITE_UNINAV_REPUTATION_URLS',
  REPUTATION_MARKETING_URLS: 'VITE_UNINAV_REPUTATION_MARKETING_URLS',
  LISTINGS_URLS: 'VITE_UNINAV_LISTINGS_URLS',
  LISTINGS_MARKETING_URLS: 'VITE_UNINAV_LISTINGS_MARKETING_URLS',
} as const;

interface ThoughtSpotState {
  authStatus: ThoughtSpotSdkAuthStatus;
  selectedRegion: Region;
  isRegionTransitioning: boolean;
  sourceTableAnswerIds: Record<Region, string>;
  datasetTableAnswerIds: Record<Region, string>;
  thoughtspotUrls: Record<Region, string>;
  apiGatewayUrls: Record<Region, string>;
  unifiedPlatformApiUrls: Record<Region, string>;
  surveyUrls: Record<Region, string>;
  digitalUrls: Record<Region, string>;
  workflowUrls: Record<Region, string>;
  pulseUrls: Record<Region, string>;
  reputationUrls: Record<Region, string>;
  listingsUrls: Record<Region, string>;
  digitalMarketingUrls: Record<Region, string>;
  pulseMarketingUrls: Record<Region, string>;
  reputationMarketingUrls: Record<Region, string>;
  listingsMarketingUrls: Record<Region, string>;
}

interface ThoughtSpotActions {
  setAuthStatus: (status: ThoughtSpotSdkAuthStatus) => void;
  setSelectedRegion: (region: Region) => void;
  initializeRegion: (payload: {
    selectedRegion: Region;
    apiGatewayUrls: Record<Region, string>;
    unifiedPlatformApiUrls: Record<Region, string>;
    thoughtspotUrls: Record<Region, string>;
    surveyUrls: Record<Region, string>;
    digitalUrls: Record<Region, string>;
    workflowUrls: Record<Region, string>;
    pulseUrls: Record<Region, string>;
    reputationUrls: Record<Region, string>;
    listingsUrls: Record<Region, string>;
    digitalMarketingUrls: Record<Region, string>;
    pulseMarketingUrls: Record<Region, string>;
    reputationMarketingUrls: Record<Region, string>;
    listingsMarketingUrls: Record<Region, string>;
  }) => void;
  setSourceTableAnswerIds: (sourceTableAnswerIds: Record<Region, string>) => void;
  setDatasetTableAnswerIds: (datasetTableAnswerIds: Record<Region, string>) => void;
  setThoughtspotUrls: (thoughtspotUrls: Record<Region, string>) => void;
  setApiGatewayUrls: (apiGatewayUrls: Record<Region, string>) => void;
  setUnifiedPlatformApiUrls: (unifiedPlatformApiUrls: Record<Region, string>) => void;
  setSurveyUrls: (surveyUrls: Record<Region, string>) => void;
  setDigitalUrls: (digitalUrls: Record<Region, string>) => void;
  setWorkflowUrls: (workflowUrls: Record<Region, string>) => void;
  setPulseUrls: (pulseUrls: Record<Region, string>) => void;
  setReputationUrls: (reputationUrls: Record<Region, string>) => void;
  setListingsUrls: (listingsUrls: Record<Region, string>) => void;
  setDigitalMarketingUrls: (digitalMarketingUrls: Record<Region, string>) => void;
  setPulseMarketingUrls: (pulseMarketingUrls: Record<Region, string>) => void;
  setReputationMarketingUrls: (reputationMarketingUrls: Record<Region, string>) => void;
  setListingsMarketingUrls: (listingsMarketingUrls: Record<Region, string>) => void;
  getCurrentSourceTableAnswerId: () => string;
  getCurrentDatasetTableAnswerId: () => string;
  getCurrentThoughtspotUrl: () => string;
  getCurrentApiGatewayUrl: () => string;
  getCurrentUnifiedPlatformApiUrl: () => string;
  getCurrentSurveyUrl: () => string;
  getCurrentDigitalUrl: (appPermissions: AppPermissions) => string;
  getCurrentDigitalBaseUrl: () => string;
  getCurrentWorkflowUrl: () => string;
  getCurrentPulseUrl: (appPermissions: AppPermissions) => string;
  getCurrentReputationUrl: (appPermissions: AppPermissions) => string;
  getCurrentListingsUrl: (appPermissions: AppPermissions) => string;
  reset: () => void;
}

export interface ThoughtSpotSlice extends ThoughtSpotState, ThoughtSpotActions {}

const createEmptyRegionConfig = (): Record<Region, string> => {
  return REGIONS.reduce(
    (acc, region) => {
      acc[region] = '';
      return acc;
    },
    {} as Record<Region, string>,
  );
};

const parseRegionConfig = (envVariableName: string): Record<Region, string> => {
  try {
    const envValue = import.meta.env[envVariableName];
    if (!envValue) {
      console.warn(`${envVariableName} environment variable is not set`);
      return createEmptyRegionConfig();
    }
    return JSON.parse(envValue);
  } catch (error) {
    console.error(`Failed to parse ${envVariableName} as JSON:`, error);
    return createEmptyRegionConfig();
  }
};

export const defaultSourceTableAnswerIds = parseRegionConfig(ENV_VARS.SOURCETABLE_ANSWER_IDS);
export const altSourceTableAnswerIds = parseRegionConfig(ENV_VARS.ALT_SOURCETABLE_ANSWER_IDS);
export const defaultDatasetTableAnswerIds = parseRegionConfig(ENV_VARS.DATASETTABLE_ANSWER_IDS);
export const altDatasetTableAnswerIds = parseRegionConfig(ENV_VARS.ALT_DATASETTABLE_ANSWER_IDS);

const sourceTableAnswerIds = defaultSourceTableAnswerIds;
const datasetTableAnswerIds = defaultDatasetTableAnswerIds;
const thoughtspotUrls = parseRegionConfig(ENV_VARS.THOUGHTSPOT_URLS);
const apiGatewayUrls = parseRegionConfig(ENV_VARS.API_GATEWAY_URLS);
const unifiedPlatformApiUrls = parseRegionConfig(ENV_VARS.UNIFIED_PLATFORM_API_URLS);
const surveyUrls = parseRegionConfig(ENV_VARS.SURVEY_URLS);
const digitalUrls = parseRegionConfig(ENV_VARS.DIGITAL_URLS);
const workflowUrls = parseRegionConfig(ENV_VARS.WORKFLOW_URLS);
const pulseUrls = parseRegionConfig(ENV_VARS.PULSE_URLS);
const reputationUrls = parseRegionConfig(ENV_VARS.REPUTATION_URLS);
const listingsUrls = parseRegionConfig(ENV_VARS.LISTINGS_URLS);
const digitalMarketingUrls = parseRegionConfig(ENV_VARS.DIGITAL_MARKETING_URLS);
const pulseMarketingUrls = parseRegionConfig(ENV_VARS.PULSE_MARKETING_URLS);
const reputationMarketingUrls = parseRegionConfig(ENV_VARS.REPUTATION_MARKETING_URLS);
const listingsMarketingUrls = parseRegionConfig(ENV_VARS.LISTINGS_MARKETING_URLS);

const initialThoughtSpotState: ThoughtSpotState = {
  authStatus: ThoughtSpotSdkAuthStatus.Unauthenticated,
  selectedRegion: (import.meta.env.VITE_DEFAULT_REGION || 'shared-dev') as Region,
  isRegionTransitioning: true,
  sourceTableAnswerIds,
  datasetTableAnswerIds,
  thoughtspotUrls,
  apiGatewayUrls,
  unifiedPlatformApiUrls,
  surveyUrls,
  digitalUrls,
  workflowUrls,
  pulseUrls,
  reputationUrls,
  listingsUrls,
  digitalMarketingUrls,
  pulseMarketingUrls,
  reputationMarketingUrls,
  listingsMarketingUrls,
};

export const createThoughtSpotSlice: StateCreator<
  ThoughtSpotSlice,
  [['zustand/devtools', never]],
  [],
  ThoughtSpotSlice
> = (set, get) => ({
  ...initialThoughtSpotState,
  setAuthStatus: (authStatus: ThoughtSpotSdkAuthStatus) => {
    set({ authStatus }, false, 'thoughtspot/setSdkAuthStatus');
  },
  setSelectedRegion: (selectedRegion: Region) => {
    set({ selectedRegion }, false, 'thoughtspot/setSelectedRegion');
  },
  initializeRegion: (payload) => {
    set(
      {
        selectedRegion: payload.selectedRegion,
        apiGatewayUrls: payload.apiGatewayUrls,
        unifiedPlatformApiUrls: payload.unifiedPlatformApiUrls,
        thoughtspotUrls: payload.thoughtspotUrls,
        surveyUrls: payload.surveyUrls,
        digitalUrls: payload.digitalUrls,
        workflowUrls: payload.workflowUrls,
        pulseUrls: payload.pulseUrls,
        reputationUrls: payload.reputationUrls,
        listingsUrls: payload.listingsUrls,
        digitalMarketingUrls: payload.digitalMarketingUrls,
        pulseMarketingUrls: payload.pulseMarketingUrls,
        reputationMarketingUrls: payload.reputationMarketingUrls,
        listingsMarketingUrls: payload.listingsMarketingUrls,
        isRegionTransitioning: false,
      },
      false,
      'thoughtspot/initializeRegion',
    );
  },
  setSourceTableAnswerIds: (sourceTableAnswerIds: Record<Region, string>) => {
    set({ sourceTableAnswerIds }, false, 'thoughtspot/setSourceTableAnswerIds');
  },
  setDatasetTableAnswerIds: (datasetTableAnswerIds: Record<Region, string>) => {
    set({ datasetTableAnswerIds }, false, 'thoughtspot/setDatasetTableAnswerIds');
  },
  setThoughtspotUrls: (thoughtspotUrls: Record<Region, string>) => {
    set({ thoughtspotUrls }, false, 'thoughtspot/setThoughtspotUrls');
  },
  setApiGatewayUrls: (apiGatewayUrls: Record<Region, string>) => {
    set({ apiGatewayUrls }, false, 'thoughtspot/setApiGatewayUrls');
  },
  setUnifiedPlatformApiUrls: (unifiedPlatformApiUrls: Record<Region, string>) => {
    set({ unifiedPlatformApiUrls }, false, 'thoughtspot/setUnifiedPlatformApiUrls');
  },
  setSurveyUrls: (surveyUrls: Record<Region, string>) => {
    set({ surveyUrls }, false, 'thoughtspot/setSurveyUrls');
  },
  setDigitalUrls: (digitalUrls: Record<Region, string>) => {
    set({ digitalUrls }, false, 'thoughtspot/setDigitalUrls');
  },
  setWorkflowUrls: (workflowUrls: Record<Region, string>) => {
    set({ workflowUrls }, false, 'thoughtspot/setWorkflowUrls');
  },
  setPulseUrls: (pulseUrls: Record<Region, string>) => {
    set({ pulseUrls }, false, 'thoughtspot/setPulseUrls');
  },
  setReputationUrls: (reputationUrls: Record<Region, string>) => {
    set({ reputationUrls }, false, 'thoughtspot/setReputationUrls');
  },
  setListingsUrls: (listingsUrls: Record<Region, string>) => {
    set({ listingsUrls }, false, 'thoughtspot/setListingsUrls');
  },
  setDigitalMarketingUrls: (digitalMarketingUrls: Record<Region, string>) => {
    set({ digitalMarketingUrls }, false, 'thoughtspot/setDigitalMarketingUrls');
  },
  setPulseMarketingUrls: (pulseMarketingUrls: Record<Region, string>) => {
    set({ pulseMarketingUrls }, false, 'thoughtspot/setPulseMarketingUrls');
  },
  setReputationMarketingUrls: (reputationMarketingUrls: Record<Region, string>) => {
    set({ reputationMarketingUrls }, false, 'thoughtspot/setReputationMarketingUrls');
  },
  setListingsMarketingUrls: (listingsMarketingUrls: Record<Region, string>) => {
    set({ listingsMarketingUrls }, false, 'thoughtspot/setListingsMarketingUrls');
  },
  getCurrentSourceTableAnswerId: () => {
    const { selectedRegion, sourceTableAnswerIds } = get();
    return sourceTableAnswerIds[selectedRegion] || '';
  },
  getCurrentDatasetTableAnswerId: () => {
    const { selectedRegion, datasetTableAnswerIds } = get();
    return datasetTableAnswerIds[selectedRegion] || '';
  },
  getCurrentThoughtspotUrl: () => {
    const { selectedRegion, thoughtspotUrls } = get();
    return thoughtspotUrls[selectedRegion] || '';
  },
  getCurrentApiGatewayUrl: () => {
    const { selectedRegion, apiGatewayUrls } = get();
    const url = apiGatewayUrls[selectedRegion];
    if (!url) {
      console.warn(`[IgniteAxiosClient] No API Gateway URL for current region: ${selectedRegion}`);
    }
    return url || '';
  },
  getCurrentUnifiedPlatformApiUrl: () => {
    const { selectedRegion, unifiedPlatformApiUrls } = get();
    const url = unifiedPlatformApiUrls[selectedRegion];
    if (!url) {
      console.warn(`[UnifiedPlatform] No API base URL for current region: ${selectedRegion}`);
    }
    return url || '';
  },
  getCurrentSurveyUrl: () => {
    const { selectedRegion, surveyUrls } = get();
    return surveyUrls[selectedRegion] || '';
  },
  getCurrentDigitalUrl: (appPermissions: AppPermissions) => {
    const { selectedRegion, digitalUrls, digitalMarketingUrls } = get();

    if (!appPermissions?.digital) {
      return digitalMarketingUrls[selectedRegion] || '';
    }

    return digitalUrls[selectedRegion] || '';
  },
  // Region-aware base URL for deep links into the digital app (e.g. apps/{appId}/surveys_2.0/{interactionId}).
  // Unlike getCurrentDigitalUrl, this never falls back to the marketing URL, since a deep link must point at the app.
  getCurrentDigitalBaseUrl: () => {
    const { selectedRegion, digitalUrls } = get();
    return digitalUrls[selectedRegion] || '';
  },
  getCurrentWorkflowUrl: () => {
    const { selectedRegion, workflowUrls } = get();
    return workflowUrls[selectedRegion] || '';
  },
  getCurrentPulseUrl: (appPermissions: AppPermissions) => {
    const { selectedRegion, pulseUrls, pulseMarketingUrls } = get();

    if (!appPermissions?.pulse) {
      return pulseMarketingUrls[selectedRegion] || '';
    }

    return pulseUrls[selectedRegion] || '';
  },
  getCurrentReputationUrl: (appPermissions: AppPermissions) => {
    const { selectedRegion, reputationUrls, reputationMarketingUrls } = get();

    if (!appPermissions?.reputation) {
      return reputationMarketingUrls[selectedRegion] || '';
    }

    return reputationUrls[selectedRegion] || '';
  },
  getCurrentListingsUrl: (appPermissions: AppPermissions) => {
    const { selectedRegion, listingsUrls, listingsMarketingUrls } = get();

    if (!appPermissions?.listings) {
      return listingsMarketingUrls[selectedRegion] || '';
    }

    return listingsUrls[selectedRegion] || '';
  },
  reset: () => {
    set(initialThoughtSpotState, false, 'thoughtspot/reset');
  },
});
